import { z } from 'zod';

/**
 * Every legal record, answer and fixture states where it came from.
 * `demo` records must always render a DEMO label and can never be exported (ADR-004 A).
 */
export const DataOriginSchema = z.enum(['demo', 'live']);
export type DataOrigin = z.infer<typeof DataOriginSchema>;

export const ApiErrorCodeSchema = z.enum([
  'UNAUTHENTICATED',
  'FORBIDDEN',
  'NOT_FOUND',
  'VALIDATION_FAILED',
  'CONFLICT',
  'NOT_IMPLEMENTED',
  'UNAVAILABLE',
  'RATE_LIMITED',
  'INTERNAL',
]);
export type ApiErrorCode = z.infer<typeof ApiErrorCodeSchema>;

export type ApiSuccess<T> = { data: T; requestId: string; warnings: string[] };
export type ApiFailure = { error: { code: ApiErrorCode; message: string }; requestId: string };
export type ApiResponse<T> = ApiSuccess<T> | ApiFailure;

export const HealthSchema = z.object({
  status: z.literal('ok'),
  service: z.literal('buakei-api'),
  environment: z.enum(['local', 'preview', 'production']),
});
export type Health = z.infer<typeof HealthSchema>;

/** Capability flags. A flag is only `live` when the backing system genuinely exists. */
export const CapabilityStateSchema = z.enum(['live', 'demo', 'preview', 'unavailable']);
export type CapabilityState = z.infer<typeof CapabilityStateSchema>;

export const FeatureFlagsSchema = z.object({
  authentication: CapabilityStateSchema,
  legalResearch: CapabilityStateSchema,
  aiAssistant: CapabilityStateSchema,
  drafting: CapabilityStateSchema,
  documentUploads: CapabilityStateSchema,
  learningTutor: CapabilityStateSchema,
  editorialDecisions: CapabilityStateSchema,
  payments: CapabilityStateSchema,
  exports: CapabilityStateSchema,
});
export type FeatureFlags = z.infer<typeof FeatureFlagsSchema>;
